/**
 * Contexte Documentaire IA — résolution multi-docs par date.
 * Affiche score de confiance + suggestions d'update + conflits détectés.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Brain, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  denomination: "Dénomination", forme_juridique: "Forme juridique",
  capital_social: "Capital social", siret: "SIRET", siren: "SIREN",
  rcs_ville: "RCS Ville", numero_tva: "N° TVA", siege_adresse: "Siège social",
  code_ape: "Code APE", representant_legal: "Représentant légal",
  representant_titre: "Titre", date_creation: "Date création",
  cloture_exercice: "Clôture exercice", activite_principale: "Activité principale",
};

const DOC_BADGE = {
  kbis: "bg-emerald-100 text-emerald-700",
  statuts: "bg-blue-100 text-blue-700",
  pv_ago: "bg-indigo-100 text-indigo-700",
  pv_age: "bg-purple-100 text-purple-700",
  bilan: "bg-amber-100 text-amber-700",
  cession_parts: "bg-rose-100 text-rose-700",
  depot_greffe: "bg-slate-100 text-slate-700",
  autre: "bg-slate-100 text-slate-500",
};

const formatDateFR = (iso) => {
  if (!iso) return "—";
  try { const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
};

const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("fr-FR");
  return String(v).slice(0, 80);
};

export default function ContextEngineCard({ onProfileUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null); // 'all' | field name

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/legal/context/resolved"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyAll = async () => {
    setApplying("all");
    try {
      const r = await api.post("/legal/context/apply", {});
      if (r.data.applied > 0) toast.success(`${r.data.applied} champ(s) mis à jour`);
      else toast.info(r.data.message || "Rien à appliquer");
      onProfileUpdated?.();
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(null); }
  };

  const applyOne = async (field) => {
    setApplying(field);
    try {
      const r = await api.post("/legal/context/apply", { fields: [field] });
      toast.success(`${FIELD_LABELS[field] || field} mis à jour`);
      onProfileUpdated?.();
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(null); }
  };

  if (loading && !data) return null;
  if (!data) return null;

  const hasUpdates = (data.suggested_updates || []).length > 0;
  const scoreColor = data.confidence_score >= 70 ? "text-emerald-600"
                     : data.confidence_score >= 40 ? "text-amber-600" : "text-red-600";

  return (
    <Card className="border-indigo-200" data-testid="context-engine-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Brain className="w-4 h-4 text-indigo-600" />
            Contexte documentaire IA
            <span className="text-xs text-slate-500 font-normal">
              Résolution multi-docs par date la plus récente
            </span>
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className={`text-2xl font-bold ${scoreColor}`}>{data.confidence_score}%</p>
              <p className="text-[10px] text-slate-500 uppercase">Confiance</p>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="p-2 bg-slate-50 rounded text-center">
            <p className="font-bold text-slate-900">{data.extractions_count}</p>
            <p className="text-slate-500 text-[10px] uppercase mt-0.5">Extractions</p>
          </div>
          <div className="p-2 bg-slate-50 rounded text-center">
            <p className="font-bold text-slate-900">{data.fields_covered}/{data.fields_total}</p>
            <p className="text-slate-500 text-[10px] uppercase mt-0.5">Champs couverts</p>
          </div>
          <div className="p-2 bg-slate-50 rounded text-center">
            <p className="font-bold text-slate-900">{data.suggested_updates?.length || 0}</p>
            <p className="text-slate-500 text-[10px] uppercase mt-0.5">Suggestions</p>
          </div>
          <div className="p-2 bg-slate-50 rounded text-center">
            <p className={`font-bold ${data.conflicts_count > 0 ? "text-amber-600" : "text-slate-900"}`}>
              {data.conflicts_count}
            </p>
            <p className="text-slate-500 text-[10px] uppercase mt-0.5">Conflits</p>
          </div>
        </div>

        {data.extractions_count === 0 ? (
          <p className="text-sm text-slate-500 flex items-center gap-2 p-3 bg-slate-50 rounded">
            <AlertTriangle className="w-4 h-4" />
            Aucune extraction OCR archivée. Lancez l'OCR IA depuis le Coffre-fort pour alimenter le contexte.
          </p>
        ) : !hasUpdates ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2 p-3 bg-emerald-50 rounded">
            <CheckCircle2 className="w-4 h-4" />
            Le profil est aligné avec les documents les plus récents.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-600 uppercase">Suggestions d'update</p>
              <Button size="sm" onClick={applyAll} disabled={applying !== null} data-testid="context-apply-all-btn">
                {applying === "all" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Tout appliquer ({data.suggested_updates.length})
              </Button>
            </div>
            <div className="border rounded divide-y max-h-80 overflow-y-auto">
              {data.suggested_updates.map((u) => (
                <div key={u.field} className="px-3 py-2" data-testid={`context-update-${u.field}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{FIELD_LABELS[u.field] || u.field}</span>
                      {u.conflict && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />conflit
                        </Badge>
                      )}
                      <Badge className={`text-[10px] ${DOC_BADGE[u.source_doc_type] || DOC_BADGE.autre}`}>
                        {u.source_doc_type}
                      </Badge>
                      <span className="text-[10px] text-slate-500">{formatDateFR(u.source_date)}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => applyOne(u.field)} disabled={applying !== null}>
                      {applying === u.field ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                    <div className="text-slate-400">
                      <span className="uppercase text-[9px]">Actuel</span> : <span className="line-through">{formatValue(u.current_value)}</span>
                    </div>
                    <div className="text-emerald-700 font-medium">
                      <span className="uppercase text-[9px] text-slate-500">Proposé</span> : {formatValue(u.suggested_value)}
                    </div>
                  </div>
                  {u.source_filename && <p className="text-[10px] text-slate-400 mt-0.5">Source : {u.source_filename}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
